import { Matrix, Ray, Vector3 } from '@babylonjs/core';
import { BabylonjsModule } from '@modules/babylonjs/src/BabylonjsModule';
import { BaseSystem, Layers, SystemOperationDefinition, Zed } from '@modules/core';
import { clearArchetype, ZEDCamera } from '@modules/standard';
import { With, World } from 'miniplex';

import { GisEntity, ItemHoGuiResponse } from '..';
import {
  onUpdateDongGUI,
  onUpdateFacilityGUI,
  onUpdateGisComplexGUI,
  onUpdateItemHoGUI,
  onUpdateSunPositionGUI,
} from '../../observable/TourDanjiObservable';
import { DongGuiResponse } from '../component/response/Dong';
import { FacilityGuiResponse } from '../component/response/Facility';
import { GisComplexGuiResponse } from '../component/response/GisComplex';
import { SunPositionGuiResponse } from '../component/response/Sun';
import { Entity as BaseEntity } from '../Entity';

import { RaycastSystem } from './RaycastSystem';
import { SunEntity } from './SunSystem';

type Entity = GisEntity & SunEntity & BaseEntity;

/**
 * 설명 추가 필요
 *
 * @group System
 * @category System
 */
export class GUIDisplaySystem implements BaseSystem {
  static readonly INJECTABLE_TOKEN = Symbol.for('GUIDisplaySystem');

  //------------------------------------------------------------------------------------
  // #### System Base ####
  //------------------------------------------------------------------------------------
  private readonly scene;
  private readonly engine;
  private readonly raycastSystem;

  private readonly gisComplexGUIArchetype;
  private readonly dongGUIArchetype;
  private readonly facilityGUIArchetype;
  private readonly itemHoGUIArchetype;
  private readonly sunPositionGUIArchetype;
  //-----------------------
  constructor({
    [Zed.WORLD_TOKEN]: world,
    [BabylonjsModule.INJECTABLE_TOKEN]: babylonjs,
    [RaycastSystem.INJECTABLE_TOKEN]: raycastSystem,
  }: {
    [Zed.WORLD_TOKEN]: World<Entity>;
    [BabylonjsModule.INJECTABLE_TOKEN]: Pick<BabylonjsModule, 'scene'>;
    [RaycastSystem.INJECTABLE_TOKEN]: RaycastSystem;
  }) {
    this.scene = babylonjs.scene;
    this.engine = this.scene.getEngine();
    this.raycastSystem = raycastSystem;

    this.gisComplexGUIArchetype = world.with('GisComplex', 'GUIDisplayable');
    this.dongGUIArchetype = world.with('Dong', 'GUIDisplayable');
    this.facilityGUIArchetype = world.with('Facility', 'GUIDisplayable');
    this.itemHoGUIArchetype = world.with('ItemHo', 'GUIDisplayable');
    this.sunPositionGUIArchetype = world.with('SunPosition', 'GUIDisplayable');
  }

  dispose() {
    clearArchetype(this.dongGUIArchetype);
    clearArchetype(this.facilityGUIArchetype);
  }

  // --------------------------------
  public afterRenderExecute = () => {
    if (onUpdateFacilityGUI.hasObservers()) {
      onUpdateFacilityGUI.notifyObservers(
        this.getGUIResponse('facility', this.facilityGUIArchetype.entities) as FacilityGuiResponse[]
      );
    }
    if (onUpdateDongGUI.hasObservers()) {
      onUpdateDongGUI.notifyObservers(this.getGUIResponse('dong', this.dongGUIArchetype.entities) as DongGuiResponse[]);
    }
    if (onUpdateGisComplexGUI.hasObservers()) {
      onUpdateGisComplexGUI.notifyObservers(
        this.getGUIResponse('gisComplex', this.gisComplexGUIArchetype.entities) as GisComplexGuiResponse[]
      );
    }
    if (onUpdateItemHoGUI.hasObservers()) {
      onUpdateItemHoGUI.notifyObservers(
        this.getGUIResponse('itemHo', this.itemHoGUIArchetype.entities) as ItemHoGuiResponse[]
      );
    }
    if (onUpdateSunPositionGUI.hasObservers()) {
      onUpdateSunPositionGUI.notifyObservers(
        this.getGUIResponse('sunPosition', this.sunPositionGUIArchetype.entities) as SunPositionGuiResponse[]
      );
    }
  };

  private getViewport() {
    const camera = this.scene.activeCamera!;

    const scalingLevel = this.engine.getHardwareScalingLevel();
    return camera.viewport.toGlobal(
      this.engine.getRenderWidth() * scalingLevel,
      this.engine.getRenderHeight() * scalingLevel
    );
  }
  private getIconScaleOffsetViaCameraDistance = () => {
    const camera = this.scene.activeCamera;
    if (camera && camera instanceof ZEDCamera) {
      const radius = camera.radius;
      const minRadius = camera.lowerRadiusLimit;
      const maxRadius = camera.upperRadiusLimit;
      if (minRadius === null || maxRadius === null) {
        return 0;
      } else {
        const radiusGap = maxRadius - minRadius;
        return radiusGap === 0 ? 0 : 1 - Math.min(1, (radius - minRadius) / radiusGap);
      }
    } else {
      return 0;
    }
  };

  private caculateRay?: Ray;
  private queueData = {
    dong: {
      oneQueueMaxCount: 20,
      currentQueue: 0,
    },
    facility: {
      oneQueueMaxCount: 10,
      currentQueue: 0,
    },
    gisComplex: {
      oneQueueMaxCount: 10,
      currentQueue: 0,
    },
    itemHo: {
      oneQueueMaxCount: 10,
      currentQueue: 0,
    },
    sunPosition: {
      oneQueueMaxCount: 10,
      currentQueue: 0,
    },
  };

  private getVisible = (entityId: string, position: Vector3, offset?: Vector3, ignoreOctreeId?: string) => {
    const camera = this.scene.activeCamera;
    if (camera && camera instanceof ZEDCamera) {
      const cameraPosition = camera.globalPosition;
      const distance = Vector3.Distance(camera.globalPosition, position);
      const direction = offset
        ? position.add(offset).subtract(cameraPosition).normalize()
        : position.subtract(cameraPosition).normalize();
      if (!this.caculateRay) {
        this.caculateRay = new Ray(cameraPosition, direction, distance - 10);
      } else {
        this.caculateRay.origin = cameraPosition;
        this.caculateRay.direction = direction;
        this.caculateRay.length = distance - 10;
      }
      return this.raycastSystem.isOccludedWithRayByOctree(entityId, this.caculateRay, ignoreOctreeId);
    }

    return true;
  };
  private getZIndex = (position: Vector3) => {
    const camera = this.scene.activeCamera;
    if (camera && camera instanceof ZEDCamera) {
      const distance = Vector3.Distance(camera.globalPosition, position);
      return Layers.BABYLON_CANVAS + Math.floor(camera.maxZ - distance);
    }
    return 0;
  };
  private cacheVisible: Map<string, boolean> = new Map();

  private getGUIResponse(key: keyof typeof this.queueData, entities: With<Entity, 'GUIDisplayable'>[]) {
    const viewport = this.getViewport();
    const sceneTransformMatrix = this.scene.getTransformMatrix();

    const queueData = this.queueData[key];

    const oneQueueMaxCount = queueData.oneQueueMaxCount;

    const length = entities.length;
    const divideQueue = length > oneQueueMaxCount ? Math.floor(length / oneQueueMaxCount) : 1;
    const step = oneQueueMaxCount;
    const remain = length - oneQueueMaxCount * (divideQueue - 1);
    const totalCount = step * (divideQueue - 1) + remain;

    let finishFrame = queueData.currentQueue === divideQueue - 1;
    const start = step * queueData.currentQueue;
    const end = finishFrame ? totalCount : step * (queueData.currentQueue + 1);

    for (let i = start; i < end; i++) {
      const entity = entities[i]!;
      const entityId = entity.entityId;
      const position = entity.GUIDisplayable.position;
      const visibleByOcclusion = entity.GUIDisplayable.occludee
        ? this.getVisible(entityId, position, entity.GUIDisplayable.offset, entity?.GUIDisplayable.ignoreOctreeId)
        : true;
      const visibleByHide = !entity.GUIDisplayable.hide && (entity.TransformNode?.isEnabled() ?? true);
      this.cacheVisible.set(entityId, visibleByOcclusion && visibleByHide);
    }

    let result: any[] = [];
    for (let i = 0; i < entities.length; i++) {
      const entity = entities[i]!;
      const entityId = entity.entityId;

      const visible = this.cacheVisible.get(entityId) ?? true;
      if (!visible) continue;

      let { position, closestXZPointToCamera } = entity.GUIDisplayable;
      const camera = this.scene.activeCamera!;

      if (closestXZPointToCamera) {
        const cameraXZ = new Vector3(camera.globalPosition.x, position.y, camera.globalPosition.z);
        const ray = new Ray(cameraXZ, position.subtract(cameraXZ).normalize(), Vector3.Distance(position, cameraXZ));
        const hit = ray
          .intersectsMeshes(closestXZPointToCamera.meshes as any, false)
          .sort((a, b) => a.distance - b.distance)[0];
        if (hit) {
          position = hit.pickedPoint!;
        }
      }

      const coordinates = Vector3.Project(position, Matrix.Identity(), sceneTransformMatrix, viewport);

      const directionToPosition = position.subtract(camera.globalPosition).normalize();
      const dotProduct = Vector3.Dot(directionToPosition, camera.getDirection(Vector3.LeftHandedForwardReadOnly));
      const isInFrontOfCamera = dotProduct > 0; // check if it is in front of the camera
      const {
        minX: clipMinX = true,
        maxX: clipMaxX = true,
        minY: clipMinY = true,
        maxY: clipMaxY = true,
        minZ: clipMinZ = true,
        maxZ: clipMaxZ = true,
      } = entity.GUIDisplayable.clip ?? {};
      const inCameraView =
        (!clipMinX || coordinates.x >= viewport.x) &&
        (!clipMaxX || coordinates.x <= viewport.width) &&
        (!clipMinY || coordinates.y >= viewport.y) &&
        (!clipMaxY || coordinates.y <= viewport.height) &&
        (!clipMinZ || isInFrontOfCamera);
      // FIXME: https://forum.babylonjs.com/t/vector3-project-incorrectly-projects-behind-inverse/30601
      // (!clipMinZ || coordinates.z >= 0) &&
      // (!clipMaxZ || coordinates.z <= 1);

      const baseGui = {
        key: entityId,
        object: {
          position: position,
          rotation: Vector3.Zero(),
          forward: Vector3.Forward(),
        },
        x: coordinates.x,
        y: coordinates.y,
        iconScaleOffset: this.getIconScaleOffsetViaCameraDistance(),
        zIndex: this.getZIndex(position),
        isVisible: inCameraView,
      };

      if (key === 'facility' && entity.Facility) {
        result.push({
          ...baseGui,
          object: {
            ...baseGui.object,
            facilityName: entity.Facility.facilityName,
          },
        } as FacilityGuiResponse);
      } else if (key === 'dong' && entity.Dong) {
        result.push({
          ...baseGui,
          object: {
            ...baseGui.object,
            dongName: entity.Dong.dongName,
            dongId: entity.Dong.buildingId,
            dongFloor: `${entity.Dong.dongFloors}F`,
          },
        } as DongGuiResponse);
      } else if (key === 'gisComplex' && entity.GisComplex) {
        result.push({
          ...baseGui,
          object: {
            ...baseGui.object,
            id: entity.GisComplex.areaDanjiId,
            name: entity.GisComplex.areaDanjiName,
          },
        } as GisComplexGuiResponse);
      } else if (key === 'itemHo' && entity.ItemHo) {
        result.push({
          ...baseGui,
          object: {
            ...baseGui.object,
            hoName: entity.ItemHo.meta.호,
          },
        } as ItemHoGuiResponse);
      } else if (key === 'sunPosition' && entity.SunPosition) {
        result.push({
          ...baseGui,
          object: {
            ...baseGui.object,
            ...entity.SunPosition,
          },
        } as SunPositionGuiResponse);
      }
    }

    queueData.currentQueue++;
    if (queueData.currentQueue === divideQueue) {
      queueData.currentQueue = 0;
    }

    if (key === 'facility') return result as FacilityGuiResponse[];
    else if (key === 'dong') return result as DongGuiResponse[];
    else if (key === 'gisComplex') return result as GisComplexGuiResponse[];
    else if (key === 'itemHo') return result as ItemHoGuiResponse[];
    else if (key === 'sunPosition') return result as SunPositionGuiResponse[];

    return [];
  }

  readonly operations: SystemOperationDefinition[] = [
    {
      group: 'afterRender',
      run: this.afterRenderExecute,
    },
  ];
}
